import { useState } from 'react';
import { getProvinces } from '../../data/regions';

// 报价类型
const QUOTE_TYPES = [
  { key: 'decoration', label: '装修报价', icon: 'ri-home-4-line' },
  { key: 'custom', label: '全屋定制', icon: 'ri-layout-line' },
  { key: 'furniture', label: '成品家具', icon: 'ri-sofa-line' },
  { key: 'appliance', label: '全屋家电', icon: 'ri-tv-line' }
];

// 快捷问题
const QUICK_QUESTIONS = [
  '帮我制定在一线城市100平米的装修预算',
  '帮我制定3房2厅1卫1厨的装修预算',
  '帮我制定140平米房子装修的成品家具、软装预算',
  '帮我制定140平米房子全屋家电、智能家居的预算'
];

interface AIAssistantModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit?: (message: string) => void;  // 可选，如果提供则直接调用，否则跳转
}

export default function AIAssistantModal({ show, onClose, onSubmit }: AIAssistantModalProps) {
  const [activeQuoteType, setActiveQuoteType] = useState('decoration');
  const [showCitySelector, setShowCitySelector] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  
  const [formData, setFormData] = useState({
    city: '北京市',
    area: '',
    rooms: '',
    halls: '',
    bathrooms: '',
    kitchens: '',
    houseType: '精装房',
    decorationLevel: '舒适',
    packageType: '全包',
    extra: ''
  });

  // 生成报价请求消息
  const generateQuoteMessage = () => {
    const { city, area, rooms, halls, bathrooms, kitchens, houseType, decorationLevel, packageType, extra } = formData;
    
    if (!area) {
      alert('请填写房屋面积');
      return null;
    }

    let baseMsg = `我的家在${city}，房屋套内面积${area}平方`;
    
    if (rooms || halls || bathrooms || kitchens) {
      baseMsg += `，户型${rooms || '0'}房${halls || '0'}厅${bathrooms || '0'}卫${kitchens || '0'}厨`;
    }

    switch (activeQuoteType) {
      case 'decoration':
        baseMsg += `，${houseType}，装修程度${decorationLevel}，选择${packageType}方式。请帮我制定我家整体的装修预算，请以表格形式展示各项费用明细。`;
        break;
      case 'custom':
        baseMsg += `，装修程度${decorationLevel}，需要全屋定制，帮我制定预算，请以表格形式展示各项费用明细。`;
        break;
      case 'furniture':
        baseMsg += `，装修程度${decorationLevel}，需要成品家具，帮我制定预算，请以表格形式展示各项费用明细。`;
        break;
      case 'appliance':
        baseMsg += `，装修程度${decorationLevel}，需要全屋家电，帮我制定预算，请以表格形式展示各项费用明细。`;
        break;
    }

    if (extra) {
      baseMsg += ` 其他补充：${extra}`;
    }

    return baseMsg;
  };

  // 提交报价请求
  const submitQuote = () => {
    const msg = generateQuoteMessage();
    if (!msg) return;
    
    if (onSubmit) {
      // 在 AI Chat 页面，直接调用回调
      onSubmit(msg);
    } else {
      // 在其他页面，新窗口打开 AI Chat
      window.open(`/ai-chat?message=${encodeURIComponent(msg)}`, '_blank');
    }
    onClose();
  };

  // 快捷问题
  const askQuickQuestion = (question: string) => {
    const fullQuestion = question + '，请以表格形式展示各项费用明细。';
    
    if (onSubmit) {
      // 在 AI Chat 页面，直接调用回调
      onSubmit(fullQuestion);
    } else {
      // 在其他页面，新窗口打开 AI Chat
      window.open(`/ai-chat?message=${encodeURIComponent(fullQuestion)}`, '_blank');
    }
    onClose();
  };

  // 获取省份和城市列表
  const provinces = getProvinces();
  const allCities = provinces.flatMap(p => p.cities);
  
  // 热门城市
  const hotCities = ['北京市', '上海市', '广州市', '深圳市', '成都市', '重庆市', '武汉市', '杭州市'];
  
  // 过滤城市
  const filteredCities = citySearch 
    ? allCities.filter(city => 
        city.name.includes(citySearch) || 
        provinces.find(p => p.cities.includes(city))?.name.includes(citySearch)
      )
    : allCities;

  if (!show) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[99998]" 
           onClick={onClose} />
      
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 
                      w-[900px] max-w-[95vw] max-h-[90vh]
                      bg-white rounded-2xl shadow-2xl z-[99999] 
                      flex flex-col overflow-hidden">
        
        {/* 头部 */}
        <div className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white 
                        px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <i className="ri-robot-2-line"></i>
              AI 装修助手
            </h2>
            <p className="text-sm opacity-90 mt-1">
              智能预算规划，让装修更透明
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 
                       transition-colors flex items-center justify-center"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* 表单区域 */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 overflow-y-auto">
          {/* 表单网格 */}
          <div className="grid grid-cols-5 gap-3 mb-3">
            {/* 城市选择 */}
            <div className="relative">
              <label className="text-xs text-gray-600 mb-1 block">城市</label>
              <div
                onClick={() => setShowCitySelector(!showCitySelector)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white 
                           cursor-pointer flex items-center justify-between hover:border-blue-400"
              >
                <span className="text-sm">{formData.city}</span>
                <i className="ri-arrow-down-s-line text-gray-400"></i>
              </div>
              
              {/* 城市选择器下拉 */}
              {showCitySelector && (
                <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-lg 
                                shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                  {/* 搜索框 */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="relative">
                      <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        placeholder="搜索城市"
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg 
                                   text-sm focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>
                  
                  {/* 热门城市 */}
                  <div className="p-3 border-b border-gray-200">
                    <div className="text-xs text-gray-500 mb-2">热门城市</div>
                    <div className="flex flex-wrap gap-2">
                      {hotCities.map(city => (
                        <button
                          key={city}
                          onClick={() => {
                            setFormData({ ...formData, city });
                            setShowCitySelector(false);
                            setCitySearch('');
                          }}
                          className="px-3 py-1 text-sm bg-gray-100 hover:bg-blue-50 
                                     hover:text-blue-600 rounded-md transition-colors"
                        >
                          {city.replace('市', '')}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* 城市列表 */}
                  <div className="max-h-60 overflow-y-auto p-2">
                    {filteredCities.map(city => (
                      <button
                        key={city.code}
                        onClick={() => {
                          setFormData({ ...formData, city: city.name });
                          setShowCitySelector(false);
                          setCitySearch('');
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 
                                   rounded-md transition-colors"
                      >
                        {city.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 面积 */}
            <div>
              <label className="text-xs text-gray-600 mb-1 block">套内面积</label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.area}
                  onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                  placeholder="请输入"
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg 
                             text-sm focus:outline-none focus:border-blue-400"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  ㎡
                </span>
              </div>
            </div>

            {/* 房屋类型 */}
            <div>
              <label className="text-xs text-gray-600 mb-1 block">房屋类型</label>
              <select
                value={formData.houseType}
                onChange={(e) => setFormData({ ...formData, houseType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                           text-sm focus:outline-none focus:border-blue-400 bg-white"
              >
                <option value="精装房">精装房</option>
                <option value="毛坯房">毛坯房</option>
                <option value="二手房">二手房</option>
                 
              </select>
            </div>

            {/* 装修程度 */}
            <div>
              <label className="text-xs text-gray-600 mb-1 block">装修程度</label>
              <select
                value={formData.decorationLevel}
                onChange={(e) => setFormData({ ...formData, decorationLevel: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                           text-sm focus:outline-none focus:border-blue-400 bg-white"
              >
                <option value="简装">简装</option>
                <option value="舒适">舒适</option>
                <option value="豪华">豪华</option>
              </select>
            </div>

            {/* 承包方式 */}
            <div>
              <label className="text-xs text-gray-600 mb-1 block">承包方式</label>
              <select
                value={formData.packageType}
                onChange={(e) => setFormData({ ...formData, packageType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                           text-sm focus:outline-none focus:border-blue-400 bg-white"
              >
                <option value="全包">全包</option>
                <option value="半包">半包</option>
              </select>
            </div>
          </div>

          {/* 户型 */}
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-2 block">户型</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={formData.rooms}
                onChange={(e) => setFormData({ ...formData, rooms: e.target.value })}
                placeholder="0"
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                           focus:outline-none focus:border-blue-400"
              />
              <span className="text-sm text-gray-600">室</span>
              <input
                type="number"
                value={formData.halls}
                onChange={(e) => setFormData({ ...formData, halls: e.target.value })}
                placeholder="0"
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                           focus:outline-none focus:border-blue-400"
              />
              <span className="text-sm text-gray-600">厅</span>
              <input
                type="number"
                value={formData.bathrooms}
                onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                placeholder="0"
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                           focus:outline-none focus:border-blue-400"
              />
              <span className="text-sm text-gray-600">卫</span>
              <input
                type="number"
                value={formData.kitchens}
                onChange={(e) => setFormData({ ...formData, kitchens: e.target.value })}
                placeholder="0"
                className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center
                           focus:outline-none focus:border-blue-400"
              />
              <span className="text-sm text-gray-600">厨</span>
            </div>
          </div>

          {/* 其他补充 */}
          <div className="mb-3">
            <label className="text-xs text-gray-600 mb-1 block">其他补充（选填）</label>
            <input
              type="text"
              value={formData.extra}
              onChange={(e) => setFormData({ ...formData, extra: e.target.value })}
              placeholder="装修风格偏好、档次、品牌要求等"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                         focus:outline-none focus:border-blue-400"
            />
          </div>

          {/* 报价类型和发送按钮 */}
          <div className="flex items-center gap-3">
            {QUOTE_TYPES.map(qt => (
              <button
                key={qt.key}
                onClick={() => setActiveQuoteType(qt.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
                            flex items-center gap-2 ${
                  activeQuoteType === qt.key
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-400'
                }`}
              >
                <i className={qt.icon}></i>
                <span>{qt.label}</span>
              </button>
            ))}
            <button
              onClick={submitQuote}
              className="ml-auto px-6 py-2 bg-gradient-to-r from-blue-500 to-cyan-400 
                         text-white rounded-lg font-medium shadow-md hover:shadow-lg
                         transition-all flex items-center gap-2"
            >
              <i className="ri-send-plane-fill"></i>
              <span>发送</span>
            </button>
          </div>
        </div>

        {/* 快捷问题 */}
        <div className="px-6 py-3 bg-white flex flex-wrap gap-2">
          {QUICK_QUESTIONS.map((q, i) => (
            <button
              key={i}
              onClick={() => askQuickQuestion(q)}
              className="px-3 py-1.5 bg-gray-100 hover:bg-blue-50 text-gray-700 
                         hover:text-blue-600 rounded-full text-xs transition-colors
                         flex items-center gap-1"
            >
              <span>{q}</span>
              <i className="ri-arrow-right-line text-xs"></i>
            </button>
          ))}
        </div>

        {/* 提示信息 */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <i className="ri-lightbulb-line text-yellow-500"></i>
            <span>填写信息后点击发送，AI 将为您生成详细的装修预算方案</span>
          </div>
        </div>
      </div>
    </>
  );
}
